import { useTraduccion } from '@tallerya/i18n';
import { Boton } from '@tallerya/ui';
import type { ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { esDeTaller } from './esDeTaller';

// La puerta del admin entero, como `Acceso` en la consola de plataforma (#168).
//
// Solo decide un caso: **entró, y no es de ningún taller**. Entonces no se dibuja
// nada del admin —ni el menú, ni el inicio— y se explica por qué, con la salida
// para cerrar sesión. Los demás estados (cargando, sin sesión, sin permiso) siguen
// en el layout y en cada `Guardia`, que ya los resuelven ruta por ruta.
//
// Va por fuera de los datos y de la sucursal (ver `main.tsx`): así un usuario sin
// taller no dispara las consultas de permisos y sucursales, que la API le
// rechazaría con 403.
//
// Mira solo el token. Que la base esté de acuerdo lo mira después
// `ProveedorTaller`, que también termina en esta misma pantalla si de los
// talleres del token no queda ninguno (#205).
//
// **Cerrar sesión es en Keycloak, no solo acá.** Con la sesión abierta allá,
// «Entrar» traería de vuelta al mismo usuario sin preguntar.

interface Props {
  children: ReactNode;
}

export function Acceso({ children }: Props) {
  const auth = useAuth();

  if (!auth.isAuthenticated || esDeTaller(auth.user?.profile)) return <>{children}</>;

  return <PuertaSinTaller />;
}

/** La pantalla de «este usuario no pertenece a ningún taller», con la salida. */
export function PuertaSinTaller() {
  const { t } = useTraduccion();
  const auth = useAuth();

  const perfil = auth.user?.profile;
  // `||` y no `??`: un nombre vacío también cede al usuario, como en el app bar.
  const nombre = perfil?.name || perfil?.preferred_username;

  return (
    <main className="puerta">
      <p className="puerta__marca">{t('app.marca')}</p>
      <h1>{t('admin.acceso.sinTaller.titulo')}</h1>
      <p>{t('admin.acceso.sinTaller.texto')}</p>
      {nombre && <p className="puerta__cuenta">{t('admin.acceso.sinTaller.cuenta', { nombre })}</p>}
      <Boton onClick={() => void auth.signoutRedirect()}>{t('admin.nav.cerrarSesion')}</Boton>
    </main>
  );
}
